import { useRef, useState, useEffect } from 'react';
import { books, siteConfig } from '@/mocks/data';
import { useScrollReveal } from '@/hooks/useScrollReveal';
import { getBookPublicationBadge, getBookPublicationBadgeClass, getBookPublicationBadgeLabel } from '../../../../../shared/book-publication-status';

export default function Books2026() {
  const section = useScrollReveal();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const books2026 = books
    .filter((b) => b.publicationDate.startsWith('2026'))
    .sort((a, b) => {
      return a.publicationDate.localeCompare(b.publicationDate);
    });

  const hasBooks = books2026.length > 0;

  const updateScrollButtons = () => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 2);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 2);
  };

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    updateScrollButtons();
    el.addEventListener('scroll', updateScrollButtons, { passive: true });
    return () => el.removeEventListener('scroll', updateScrollButtons);
  }, []);

  const scrollBy = (direction: 'left' | 'right') => {
    const el = scrollRef.current;
    if (!el) return;
    const cardWidth = el.firstElementChild?.getBoundingClientRect().width || 300;
    const gap = 32;
    const scrollAmount = cardWidth + gap;
    el.scrollBy({
      left: direction === 'left' ? -scrollAmount : scrollAmount,
      behavior: 'smooth',
    });
  };

  return (
    <section
      className="w-full min-h-[90svh] flex items-center px-6 md:px-10 py-20 md:py-28 bg-gradient-to-b from-primary-100/60 via-background-100 to-background-100"
      ref={section.ref}
    >
      <div
        className={`w-full max-w-6xl mx-auto transition-all duration-700 ease-out ${
          section.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}
      >
        {/* Section Header */}
        <div className="text-center mb-14">
          <div className="flex items-center justify-center gap-2.5 mb-6">
            <span className="block w-5 h-px bg-primary-400/50"></span>
            <span className="text-[13px] font-medium text-primary-600 tracking-[0.15em] uppercase whitespace-nowrap">
              最新發行
            </span>
            <span className="block w-5 h-px bg-primary-400/50"></span>
          </div>
          <h2 className="font-heading text-[2.25rem] md:text-[2.75rem] font-semibold text-foreground-950 mb-4 leading-[1.15] tracking-tight">
            2026 最新發行
          </h2>
          <p className="text-base md:text-lg text-foreground-500 max-w-2xl mx-auto leading-relaxed font-light">
            探索樂律文化於 2026 年最新發行的心理學與生活理解書籍。
          </p>
        </div>

        {/* Book Content */}
        {hasBooks ? (
          <>
            <div className="relative">
              {/* Scrollable Track */}
              <div
                ref={scrollRef}
                className="flex gap-8 overflow-x-auto scroll-smooth snap-x snap-mandatory pb-4 px-1"
                style={{
                  scrollbarWidth: 'none',
                  msOverflowStyle: 'none',
                  WebkitOverflowScrolling: 'touch',
                }}
              >
                {books2026.map((book) => (
                  <div
                    key={book.slug}
                    className="group flex-shrink-0 w-[260px] sm:w-[280px] md:w-[300px] snap-start"
                  >
                    {/* Cover Image with Category Badge */}
                    <div
                      className="aspect-[3/4] w-full rounded-sm mb-5 relative overflow-hidden transition-all duration-300 group-hover:translate-y-[-3px] group-hover:shadow-lg"
                      role="img"
                      aria-label={`${book.title} 書封`}
                    >
                      <img
                        src={book.coverImage}
                        alt={book.title}
                        width={300}
                        height={400}
                        className="w-full h-full object-cover"
                      />
                      {(() => {
                        const badge = getBookPublicationBadge(book.publicationDate);
                        return badge ? (
                          <span className={`absolute top-2 right-2 rounded-full px-2 py-0.5 text-xs font-medium ${getBookPublicationBadgeClass(badge)}`}>
                            {getBookPublicationBadgeLabel(badge)}
                          </span>
                        ) : null;
                      })()}
                      {book.category && (
                        <span className="absolute top-2.5 left-2.5 px-2.5 py-1 rounded-full text-[11px] font-medium tracking-wide bg-background-50/90 text-foreground-700 backdrop-blur-sm whitespace-nowrap">
                          {book.category}
                        </span>
                      )}
                    </div>

                    {/* Book Title — full, no truncation */}
                    <h3 className="font-heading text-[15px] md:text-base font-semibold text-foreground-900 leading-snug mb-1.5">
                      {book.title}
                    </h3>

                    {/* Subtitle */}
                    {book.subtitle && (
                      <p className="text-[13px] text-foreground-500 leading-relaxed mb-2 line-clamp-2">
                        {book.subtitle}
                      </p>
                    )}

                    {/* Author + ISBN */}
                    <div className="flex items-center gap-1.5 flex-wrap mb-4">
                      <span className="text-[13px] text-foreground-600 font-medium">
                        {book.author}
                      </span>
                      <span className="text-foreground-300 text-[11px]">·</span>
                      <span className="text-[12px] text-foreground-400 font-mono">
                        ISBN {book.isbn}
                      </span>
                    </div>

                    {/* Book Detail Link */}
                    <a
                      href={`/books/${book.slug}`}
                      className="whitespace-nowrap inline-flex items-center gap-2 px-4 py-2 rounded-full text-[13px] font-medium border border-foreground-200 text-foreground-700 hover:border-foreground-400 hover:bg-background-100 transition-all cursor-pointer"
                      aria-label={`查看《${book.title}》書籍資訊`}
                    >
                      查看書籍資訊
                      <i className="ri-arrow-right-line text-[11px]"></i>
                    </a>
                  </div>
                ))}
              </div>

              {/* Arrows — bottom right */}
              <div className="flex items-center gap-1.5 mt-5 justify-end">
                <button
                  type="button"
                  onClick={() => scrollBy('left')}
                  disabled={!canScrollLeft}
                  className={`w-9 h-9 rounded-full border transition-all cursor-pointer flex items-center justify-center ${
                    canScrollLeft
                      ? 'border-background-200 bg-background-50 hover:bg-primary-50 hover:border-primary-300 text-foreground-600'
                      : 'border-background-200 bg-background-100 text-foreground-300 cursor-default'
                  }`}
                  aria-label="向左滑動"
                >
                  <i className="ri-arrow-left-s-line text-lg"></i>
                </button>
                <button
                  type="button"
                  onClick={() => scrollBy('right')}
                  disabled={!canScrollRight}
                  className={`w-9 h-9 rounded-full border transition-all cursor-pointer flex items-center justify-center ${
                    canScrollRight
                      ? 'border-background-200 bg-background-50 hover:bg-primary-50 hover:border-primary-300 text-foreground-600'
                      : 'border-background-200 bg-background-100 text-foreground-300 cursor-default'
                  }`}
                  aria-label="向右滑動"
                >
                  <i className="ri-arrow-right-s-line text-lg"></i>
                </button>
              </div>
            </div>

            {/* Bottom Catalog Note — once per section */}
            <div className="mt-16 pt-10 border-t border-background-200/80 text-center">
              <p className="text-[14px] text-foreground-500 leading-relaxed max-w-xl mx-auto mb-6 font-light">
                完整書名、作者及相關出版資訊，以崧博書籍資料庫中的個別書籍頁為準。
              </p>
              <a
                href={siteConfig.catalogUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="whitespace-nowrap inline-flex items-center gap-2.5 px-7 py-3 rounded-full text-[15px] font-medium bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer tracking-wide"
                aria-label="前往崧博書籍資料庫查詢完整書目"
              >
                前往書籍資料庫
                <i className="ri-external-link-line text-[13px]"></i>
              </a>
            </div>
          </>
        ) : (
          <div className="max-w-xl mx-auto text-center py-10">
            <div className="w-16 h-16 rounded-full bg-background-200/60 flex items-center justify-center mx-auto mb-6">
              <i className="ri-book-open-line text-foreground-400 text-2xl"></i>
            </div>
            <p className="text-base md:text-lg text-foreground-500 leading-relaxed">
              目前尚無可顯示的2026最新發行書籍。
            </p>
            <p className="text-[13px] text-foreground-400 mt-3 leading-relaxed">
              正式書目資訊請前往崧博出版書籍資料庫查詢。
            </p>
            <div className="mt-8">
              <a
                href={siteConfig.catalogUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="whitespace-nowrap inline-flex items-center gap-2.5 px-7 py-3 rounded-full text-[15px] font-medium bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer tracking-wide"
              >
                前往書籍資料庫
                <i className="ri-external-link-line text-[13px]"></i>
              </a>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
